import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { invalidateMe } from "../lib/useMe";
import { useT } from "../lib/i18n";
import { Button } from "../components/ui/Button";

export function ResetPassword({ onAuthed }: { onAuthed: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    if (password !== repeat) {
      setErr(t("auth.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      invalidateMe();
      onAuthed();
      navigate("/", { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.message === "invalid_token") {
        setInvalid(true);
      } else {
        setErr(t("auth.genericError"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page-scroll min-h-screen-d flex flex-col items-center justify-center px-6 bg-canvas">
      <div className="w-full max-w-xs flex flex-col items-center animate-rise">
        {!token || invalid ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-fg-secondary">{t("auth.resetInvalid")}</p>
            <Link to="/forgot" className="underline text-sm">
              {t("auth.forgotLink")}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold mb-8 text-center">{t("auth.resetTitle")}</h1>
            <form onSubmit={submit} className="w-full space-y-3">
              <input
                className="input"
                type="password"
                required
                minLength={8}
                placeholder={t("auth.newPassword")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
              />
              <input
                className="input"
                type="password"
                required
                minLength={8}
                placeholder={t("auth.repeatPassword")}
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
              />
              {err && <p className="text-sm text-danger">{err}</p>}
              <Button type="submit" disabled={busy} fullWidth>
                {t("auth.resetCta")}
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
